'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useLocale } from 'next-intl';
import { useMemo, useState } from 'react';

import { registerUser, submitIpressoAgreements } from '../lib';

import ActivateAccountForm from './ActivateAccountForm';
import CompleteRegistrationForm from './CompleteRegistrationForm';
import RegisterWithEmailFormV1 from './RegisterWithEmailFormV1';

import { pushSimpleGAEvent } from '@components/Analytics/Google/GAScript';

type Step = 'register' | 'activate' | 'completeRegistration';

export default function RegisterFormV1() {
  const [step, setStep] = useState<Step>('register');
  const [email, setEmail] = useState<string | null>(null);
  const router = useRouter();
  const searchParams = useSearchParams();

  const locale = useLocale();

  const onRegisterSuccess = (_email: string) => {
    setEmail(_email);
    setStep('activate');
  };

  const onActivateSuccess = () => {
    setStep('completeRegistration');
  };

  const onOldRegisterSubmit = async (data: any, setError: (...args: any) => void) => {
    const registerResult = await registerUser(data.email, locale);
    pushSimpleGAEvent('transinfo_old_register_step_1', {
      transinfo_is_logged_in: 'false',
      transinfo_is_newsletter_user: 'false',
      transinfo_user_email: data.email,
      transinfo_user_provider: 'wordpress',
      locale: locale,
    });

    if (registerResult === 'success') {
      await submitIpressoAgreements({
        ...data,
        locale,
      });

      if (onRegisterSuccess) {
        onRegisterSuccess(data.email);
      }
      return;
    }

    // TODO: Translate errors.
    switch (registerResult) {
      case 'alreadyRegistered':
        setError('email', {
          type: 'manual',
          message: 'This email is already registered.',
        });
        break;
      case 'invalidEmail':
        setError('email', {
          type: 'manual',
          message: 'This email is invalid.',
        });
        break;
      default:
        setError('email', {
          type: 'manual',
          message: 'Something went wrong.',
        });
        break;
    }
  };

  const onCompleteSuccess = () => {
    const from = searchParams ? searchParams.get('from') : null;
    if (from) {
      router.push(`/${locale}/${from}`);
    }

    router.push(`/${locale}`);
  };

  const form = useMemo(() => {
    if (step === 'register' || !email) {
      return <RegisterWithEmailFormV1 onSubmit={onOldRegisterSubmit} className="flex flex-col" />;
    }

    if (step === 'activate') {
      return <ActivateAccountForm email={email} onSuccess={onActivateSuccess} className="flex flex-col" />;
    }

    if (step === 'completeRegistration') {
      return <CompleteRegistrationForm email={email} onSuccess={onCompleteSuccess} className="flex flex-col" />;
    }
  }, [step, email]);

  return <div className="flex flex-col bg-white p-4 dark:bg-dark-500 lg:p-8">{form}</div>;
}
